import React, { useState, useCallback, useEffect } from "react";
import { ModalV2, Button, Flex, Text, toast } from "@jsq/ds/core";
import { toDbId } from "#utils/graphql_utils";
import { EntitySelector } from "../../EntitySelector";

export interface EntityAssociationModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentEntityId: number | null;
  currentEntityName?: string | null;
  arenaId?: string;
  /** Called when user confirms selection - calls mutation immediately */
  onSave: (entityId: number | null, entityName: string) => Promise<void>;
  /** Loading state for the save mutation */
  isSaving?: boolean;
}

export const EntityAssociationModal: React.FC<EntityAssociationModalProps> = ({
  isOpen,
  onClose,
  currentEntityId,
  currentEntityName,
  arenaId,
  onSave,
  isSaving = false,
}) => {
  const [selectedEntityId, setSelectedEntityId] = useState<number | null>(currentEntityId);
  const [selectedEntityName, setSelectedEntityName] = useState<string | null>(
    currentEntityName || null,
  );

  // Reset selected entity when modal opens with new currentEntityId.
  // Intentionally syncing local state from props on open — this is a deliberate
  // "reset on open" pattern, not infinite cascading renders.

  useEffect(() => {
    if (isOpen) {
      setSelectedEntityId(currentEntityId);
      setSelectedEntityName(currentEntityName || null);
    }
  }, [isOpen, currentEntityId, currentEntityName]);

  const handleEntitySelect = useCallback((entityId: string, entityName: string) => {
    // EntitySelector returns global ID as string, we need to convert to DB ID
    const dbId = toDbId(entityId);
    setSelectedEntityId(dbId);
    setSelectedEntityName(entityName);
  }, []);

  // Call mutation immediately when user confirms selection
  const handleSave = useCallback(async () => {
    if (selectedEntityId === null || selectedEntityName === null) {
      toast.error("Please select an entity");
      return;
    }

    await onSave(selectedEntityId, selectedEntityName);
    // Note: Modal stays open to show the updated entity name
  }, [selectedEntityId, selectedEntityName, onSave]);

  const handleCancel = useCallback(() => {
    setSelectedEntityId(currentEntityId);
    setSelectedEntityName(currentEntityName || null);
    onClose();
  }, [currentEntityId, currentEntityName, onClose]);

  return (
    <ModalV2
      show={isOpen}
      onHide={handleCancel}
      title="Edit investment entity"
      data-testid="entity-association-modal"
      isCentered
      size="large"
    >
      <ModalV2.Body>
        <Flex flexDirection="column" gap={16}>
          <Text variant="secondary-md">Edit the reporting-only entity selection</Text>
          <Flex flexDirection="column" gap={4}>
            <Text variant="strong-sm">Reporting-only entity</Text>
            <EntitySelector
              arenaId={arenaId}
              selectedEntityName={selectedEntityName || undefined}
              onEntitySelect={handleEntitySelect}
              disabled={isSaving}
              showBankAccountDisplay={false}
            />
          </Flex>
        </Flex>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex gap={12} justifyContent="flex-end">
          <Button variant="neutral" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void handleSave()} isLoading={isSaving}>
            Save Changes
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};

export default EntityAssociationModal;
